import { ChevronDown } from 'lucide-react';

import type { Property } from '@/types/property';

interface PropertyPillProps {
  /** The currently selected property to display. */
  selectedProperty: Property;
  /** All available properties. When length > 1, renders as an interactive selector. */
  properties: Property[];
  /** Called when the user selects a different property. */
  onSelect: (id: string) => void;
}

/**
 * PropertyPill — compact property display pill used on map surfaces.
 *
 * Single property: display-only pill, no chevron.
 * Multi-property: pill with chevron + invisible native <select> overlay for
 * accessible native picker on iOS while keeping custom styled chrome.
 */
export function PropertyPill({ selectedProperty, properties, onSelect }: PropertyPillProps) {
  if (properties.length > 1) {
    return (
      <div className="relative">
        <div
          data-testid="property-pill"
          className="flex items-center gap-1.5 glass-cockpit rounded-full pl-3 pr-2.5 py-1.5 shadow-card-lg"
        >
          <span className="truncate text-body-sm font-medium text-bark-100 max-w-[140px]">
            {selectedProperty.name}
          </span>
          <ChevronDown
            data-testid="property-pill-chevron"
            data-constrained="true"
            className="w-4 h-4 text-bark-400 flex-shrink-0 flex-grow-0"
            aria-hidden="true"
          />
        </div>
        <select
          value={selectedProperty.id}
          onChange={(e) => onSelect(e.target.value)}
          className="absolute inset-0 w-full opacity-0 cursor-pointer"
          aria-label="Select property"
        >
          {properties.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div
      data-testid="property-pill"
      className="flex items-center glass-cockpit rounded-full px-3 py-1.5 shadow-card-lg"
    >
      <span data-truncated="true" className="truncate text-body-sm font-medium text-bark-100 max-w-[140px]">
        {selectedProperty.name}
      </span>
    </div>
  );
}
